import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE, GREEN] = CHART_COLORS;
const MAX_WIDTH = 18;
const MIN_WIDTH = 3;

/**
 * An axis-aligned arrow whose shaft thickness follows its share of the heat, with a
 * triangular head sized to match.
 */
function Flow({
  x1,
  y1,
  x2,
  y2,
  share,
  color,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  share: number;
  color: string;
}) {
  const width = Math.round(
    Math.max(MIN_WIDTH, MAX_WIDTH * Math.min(Math.max(share, 0), 1))
  );
  const dx = Math.sign(x2 - x1);
  const dy = Math.sign(y2 - y1);
  const headLength = Math.max(12, Math.round(width * 1.1));
  const half = Math.round(width * 0.9) + 4;
  const bx = x2 - dx * headLength;
  const by = y2 - dy * headLength;
  // Perpendicular to an axis-aligned direction.
  const px = dy * half;
  const py = dx * half;
  return (
    <g>
      <line
        stroke={color}
        strokeWidth={width}
        x1={x1}
        x2={bx}
        y1={y1}
        y2={by}
      />
      <polygon
        fill={color}
        points={`${x2},${y2} ${bx + px},${by + py} ${bx - px},${by - py}`}
      />
    </g>
  );
}

/**
 * The textbook heat-engine (or refrigerator) schematic: a hot reservoir above, a cold one
 * below and the machine between them. Arrow thickness shows each flow's share of the
 * largest one, so a 40% engine visibly sends 60% of the heat to the cold side.
 */
export function ReservoirDiagram({
  id,
  label,
  description,
  mode,
  hotShare,
  coldShare,
  workShare,
}: {
  id: string;
  label: string;
  description: string;
  /** `engine` runs heat downhill and extracts work; `cooler` pumps it uphill with work. */
  mode: "engine" | "cooler";
  /** Each flow as a fraction of the largest flow, 0–1. */
  hotShare: number;
  coldShare: number;
  workShare: number;
}) {
  const engine = mode === "engine";
  return (
    <ChartSvg
      description={description}
      height={290}
      id={id}
      label={label}
      width={560}
    >
      <rect
        fill={ORANGE}
        fillOpacity="0.85"
        height="44"
        rx="6"
        width="260"
        x={150}
        y={12}
      />
      <text
        fill="#ffffff"
        fontSize="18"
        fontStyle="italic"
        fontWeight="bold"
        textAnchor="middle"
        x={280}
        y={42}
      >
        T_h
      </text>
      <rect
        fill={BLUE}
        fillOpacity="0.85"
        height="44"
        rx="6"
        width="260"
        x={150}
        y={234}
      />
      <text
        fill="#ffffff"
        fontSize="18"
        fontStyle="italic"
        fontWeight="bold"
        textAnchor="middle"
        x={280}
        y={264}
      >
        T_c
      </text>
      <circle
        cx={280}
        cy={145}
        fill={CHART_INK.grid}
        r={44}
        stroke={CHART_INK.text}
        strokeWidth="2"
      />
      <text
        fill={CHART_INK.text}
        fontSize="15"
        textAnchor="middle"
        x={280}
        y={150}
      >
        M
      </text>
      <Flow
        color={ORANGE}
        share={hotShare}
        x1={280}
        x2={280}
        y1={engine ? 58 : 98}
        y2={engine ? 98 : 58}
      />
      <Flow
        color={BLUE}
        share={coldShare}
        x1={280}
        x2={280}
        y1={engine ? 192 : 232}
        y2={engine ? 232 : 192}
      />
      <Flow
        color={GREEN}
        share={workShare}
        x1={engine ? 326 : 480}
        x2={engine ? 480 : 326}
        y1={145}
        y2={145}
      />
      <text
        fill={ORANGE}
        fontSize="15"
        fontStyle="italic"
        textAnchor="start"
        x={296}
        y={84}
      >
        Q_h
      </text>
      <text
        fill={BLUE}
        fontSize="15"
        fontStyle="italic"
        textAnchor="start"
        x={296}
        y={218}
      >
        Q_c
      </text>
      <text
        fill={GREEN}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={410}
        y={134}
      >
        W
      </text>
    </ChartSvg>
  );
}
